import React, { useState, useEffect } from 'react';
import ImageUploadBox from '../../components/ImageUploadBox';
import {
  getWebNotificationById,
  createWebNotification,
  updateWebNotification
} from '../../services/webNotificationService';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, Typography, FormControlLabel,
  Switch, Box, makeStyles, CircularProgress, Snackbar,
  IconButton, MenuItem, Select, FormControl, InputLabel, Checkbox
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import WebIcon from '@material-ui/icons/Web';

const CATEGORIES = [
  'Announcement',
  'Feature Update',
  'Event',
  'Ramadan & Eid',
  'Prayer & Worship',
  'General Notice',
];

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    '& .MuiDialog-paper': {
      margin: 24,
      width: 'calc(100vw - 48px)',
      maxWidth: 'calc(100vw - 48px) !important',
      height: 'calc(100vh - 48px)',
      maxHeight: 'calc(100vh - 48px)',
      borderRadius: 20,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.type === 'dark' ? '#0f172a' : '#ffffff',
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: '0 25px 70px rgba(0,0,0,0.35)',
      [theme.breakpoints.down('xs')]: {
        margin: 8,
        width: 'calc(100vw - 16px)',
        maxWidth: 'calc(100vw - 16px) !important',
        height: 'calc(100vh - 16px)',
        maxHeight: 'calc(100vh - 16px)',
        borderRadius: 14,
      },
    },
  },
  dialogHeader: {
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? 'rgba(30, 41, 59, 0.8)' : 'rgba(248, 250, 252, 0.9)',
    backdropFilter: 'blur(10px)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },
  headerIconBadge: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    boxShadow: '0 4px 14px rgba(2, 132, 199, 0.3)',
  },
  dialogTitle: {
    margin: 0,
    fontWeight: 700,
    fontSize: '1.25rem',
    color: theme.palette.text.primary,
  },
  dialogSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
  },
  closeBtn: {
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: '#ef4444',
    },
  },
  dialogContent: {
    padding: 24,
    flex: 1,
    overflowY: 'auto',
    background: theme.palette.type === 'dark' ? '#0b1120' : '#f8fafc',
  },
  sectionCard: {
    background: theme.palette.type === 'dark' ? '#1e293b' : '#ffffff',
    borderRadius: 16,
    padding: 24,
    marginBottom: 24,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: theme.palette.primary.main,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  dialogActions: {
    padding: '16px 24px',
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? '#1e293b' : '#ffffff',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 16,
  },
}));

export default function WebNotificationFormModal({
  open,
  notificationId = null,
  prefillData = null,
  onClose,
  onSuccess,
}) {
  const classes = useStyles();
  const isEdit = Boolean(notificationId);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const [form, setForm] = useState({
    title: '',
    summary: '',
    content: '',
    category: 'Announcement',
    imageUrl: '',
    actionUrl: '',
    buttonText: 'Learn More',
    isPinned: false,
    isActive: true,
    sourcePushNotificationId: null,
  });

  useEffect(() => {
    if (!open) return;

    if (notificationId) {
      setLoading(true);
      getWebNotificationById(notificationId).then((data) => {
        if (data) {
          setForm({
            title: data.title || '',
            summary: data.summary || '',
            content: data.content || '',
            category: data.category || 'Announcement',
            imageUrl: data.imageUrl || '',
            actionUrl: data.actionUrl || '',
            buttonText: data.buttonText || 'Learn More',
            isPinned: Boolean(data.isPinned),
            isActive: data.isActive !== undefined ? Boolean(data.isActive) : true,
            sourcePushNotificationId: data.sourcePushNotificationId || null,
          });
        }
        setLoading(false);
      }).catch((err) => {
        console.error('Error loading web notification:', err);
        setLoading(false);
      });
    } else if (prefillData) {
      setForm({
        title: prefillData.title || '',
        summary: prefillData.body || prefillData.summary || '',
        content: prefillData.content || prefillData.body || '',
        category: prefillData.category || 'Announcement',
        imageUrl: prefillData.imageUrl || '',
        actionUrl: prefillData.actionUrl || '',
        buttonText: prefillData.buttonText || 'Learn More',
        isPinned: Boolean(prefillData.isPinned),
        isActive: true,
        sourcePushNotificationId: prefillData.id || null,
        saveToGallery: true,
      });
      setLoading(false);
    } else {
      setForm({
        title: '',
        summary: '',
        content: '',
        category: 'Announcement',
        imageUrl: '',
        actionUrl: '',
        buttonText: 'Learn More',
        isPinned: false,
        isActive: true,
        sourcePushNotificationId: null,
        saveToGallery: true,
      });
      setLoading(false);
    }
  }, [open, notificationId, prefillData]);

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!form.title.trim() || !form.summary.trim()) {
      setSnackbar({ open: true, message: 'Title and short summary are required.' });
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await updateWebNotification(notificationId, form);
        setSnackbar({ open: true, message: 'Web notification updated successfully!' });
      } else {
        await createWebNotification(form);
        setSnackbar({ open: true, message: 'Web notification published successfully!' });
      }
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 500);
    } catch (err) {
      console.error('Error saving web notification:', err);
      setSnackbar({ open: true, message: 'Failed to save web notification. Try again.' });
    }
    setSaving(false);
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      className={classes.dialogRoot}
      fullWidth
      maxWidth={false}
      scroll="paper"
    >
      <div className={classes.dialogHeader}>
        <div className={classes.headerLeft}>
          <div className={classes.headerIconBadge}>
            <WebIcon />
          </div>
          <div>
            <Typography className={classes.dialogTitle}>
              {isEdit ? 'Edit Web Notification' : 'Store & Publish Web Notification'}
            </Typography>
            <Typography className={classes.dialogSubtitle}>
              Announcements shown in the Noor Web notification drawer
            </Typography>
          </div>
        </div>
        <IconButton className={classes.closeBtn} onClick={() => !saving && onClose()} disabled={saving} size="small">
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent className={classes.dialogContent} dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <div className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>
                1. BASIC ANNOUNCEMENT INFO
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Title *"
                    value={form.title}
                    onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Ramadan Kareem from Noor Team"
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="category-label">Category</InputLabel>
                    <Select
                      labelId="category-label"
                      label="Category"
                      value={form.category}
                      onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
                    >
                      {CATEGORIES.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {cat}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    variant="outlined"
                    label="Short Summary *"
                    value={form.summary}
                    onChange={(e) => setForm(prev => ({ ...prev, summary: e.target.value }))}
                    placeholder="Short summary displayed in the web drawer list..."
                    helperText="Keep it under 150 characters for clean presentation."
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    variant="outlined"
                    label="Extended Article Content (Optional)"
                    value={form.content}
                    onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Optional long description or message shown when user clicks to expand."
                  />
                </Grid>
              </Grid>
            </div>

            <div className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>
                2. MEDIA & LINKS
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                    Web Notification Image
                  </Typography>
                  <ImageUploadBox
                    value={form.imageUrl}
                    onChange={(url) => setForm(prev => ({ ...prev, imageUrl: url }))}
                    folder="web_notification_images"
                    storagePath="web_notification_images"
                    initialUrl={form.imageUrl}
                    onUploadComplete={(url) => setForm(prev => ({ ...prev, imageUrl: url }))}
                    aspectRatio="16:9"
                    helperText="Recommended: 16:9 ratio banner. Optional."
                  />
                  {form.imageUrl && (
                    <Box mt={0.5} display="flex" alignItems="center">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.saveToGallery !== false}
                            onChange={(e) => setForm(prev => ({ ...prev, saveToGallery: e.target.checked }))}
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2" style={{ color: '#10b981', fontWeight: 600, fontSize: '0.85rem' }}>
                            Auto-store image into Image Gallery (ছবিটি গ্যালারিতেও সংরক্ষণ করুন)
                          </Typography>
                        }
                      />
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Target Action URL"
                    value={form.actionUrl}
                    onChange={(e) => setForm(prev => ({ ...prev, actionUrl: e.target.value }))}
                    placeholder="e.g., https://... or /quran"
                    helperText="Destination link when the user clicks the notification button."
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Button Text"
                    value={form.buttonText}
                    onChange={(e) => setForm(prev => ({ ...prev, buttonText: e.target.value }))}
                    placeholder="e.g., Learn More, View Surah"
                    helperText="Action button label shown on Noor Web."
                  />
                </Grid>
              </Grid>
            </div>

            <div className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>
                3. DISPLAY SETTINGS
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.isPinned}
                        onChange={(e) => setForm(prev => ({ ...prev, isPinned: e.target.checked }))}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>Pin to Top</Typography>
                        <Typography variant="caption" color="textSecondary">Keep this notification at the top of the user list</Typography>
                      </Box>
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.isActive}
                        onChange={(e) => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>Active (Visible)</Typography>
                        <Typography variant="caption" color="textSecondary">Uncheck to temporarily hide from Noor Web</Typography>
                      </Box>
                    }
                  />
                </Grid>
              </Grid>
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} disabled={saving} style={{ borderRadius: 10, textTransform: 'none', marginRight: 8 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSubmit}
          disabled={saving || loading}
          style={{
            borderRadius: 10,
            textTransform: 'none',
            fontWeight: 600,
            padding: '8px 24px',
            background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
            color: '#ffffff',
          }}
        >
          {saving ? 'Saving...' : (isEdit ? 'Update Web Notification' : 'Publish to Noor Web')}
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
}
